import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import SavedQuotes from '../components/SavedQuotes';
import { useQuotes } from '../context/QuotesContext';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import { Button } from '../components/ui/button';
import { buildQuotesListBackState } from '../lib/quoteListPagination';

const applyQuoteToSession = (quote, loadBackendQuoteSession, setIncludeTemplateInExport) => {
  loadBackendQuoteSession(quote);
  const storedTemplate = quote?.quote_data?.exportOptions?.includeTemplateInExport;
  const hasSignature = Boolean(quote?.quote_data?.signature || quote?.quote_data?.signed_at);
  if (typeof storedTemplate === 'boolean') {
    setIncludeTemplateInExport(storedTemplate || hasSignature);
  } else if (hasSignature) {
    // Signed quotes must show the template agreement page so the signature is visible
    setIncludeTemplateInExport(true);
  } else {
    setIncludeTemplateInExport(false);
  }
};

const DashboardQuoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { loadBackendQuoteSession, setIncludeTemplateInExport } = useQuotes();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    if (!id) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setSessionReady(false);

      const quoteFromState = location.state?.quote;

      // Always fetch fresh from API so signed_at / signature are not missing from a stale list copy
      try {
        const response = await axios.get(`${API_URL}/api/quotes/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        });
        if (cancelled) return;
        applyQuoteToSession(response.data, loadBackendQuoteSession, setIncludeTemplateInExport);
        setSessionReady(true);
      } catch (err) {
        if (cancelled) return;
        if (quoteFromState && String(quoteFromState.id) === String(id)) {
          applyQuoteToSession(quoteFromState, loadBackendQuoteSession, setIncludeTemplateInExport);
          setSessionReady(true);
          return;
        }
        const detail = err.response?.data?.detail;
        setError(
          typeof detail === 'string'
            ? detail
            : 'Failed to load quote. Restart the backend server if this is a new install.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    id,
    authLoading,
    isAuthenticated,
    navigate,
    loadBackendQuoteSession,
    setIncludeTemplateInExport,
  ]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center text-gray-600">
        Loading quote…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center gap-4 p-6">
        <p className="text-red-600 text-center max-w-md">{error}</p>
        <Button
          variant="outline"
          onClick={() =>
            navigate(location.state?.returnPath || '/dashboard?tab=quotes', {
              state: buildQuotesListBackState(
                location.state?.returnPath,
                location.state?.quotesPage,
              ),
            })
          }
        >
          Back to quotes
        </Button>
      </div>
    );
  }

  if (!sessionReady) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center gap-4 p-6">
        <p className="text-gray-600">No quote data available.</p>
        <Button
          variant="outline"
          onClick={() =>
            navigate(location.state?.returnPath || '/dashboard?tab=quotes', {
              state: buildQuotesListBackState(
                location.state?.returnPath,
                location.state?.quotesPage,
              ),
            })
          }
        >
          Back to quotes
        </Button>
      </div>
    );
  }

  return (
    <SavedQuotes
      asPage
      dashboardMode
      forceEditing
      returnPath={location.state?.returnPath || '/dashboard?tab=quotes'}
      returnQuotesPage={location.state?.quotesPage ?? null}
    />
  );
};

export default DashboardQuoteDetail;
